import React, { useContext } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Button, message } from "antd";
import {
  ShareAltOutlined,
  GiftOutlined,
  UserAddOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthProvider";
import PageHeader from "../../components/PageHeader";
import ReferralSummaryCard from "../../components/referrals/ReferralSummaryCard";
import ReferralInvitesTable from "../../components/referrals/ReferralInvitesTable";
import {
  getReferralSummary,
  getReferralInvites,
} from "../../lib/firebase/referralModel";
import { propertyKeys } from "../../lib/queryKeys";

const Referrals = () => {
  const { user } = useContext(AuthContext);

  // Load referral summary
  const {
    data: referralSummary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ["referralSummary", user?.uid],
    queryFn: getReferralSummary,
    enabled: !!user?.uid,
  });

  // Load referral invites
  const {
    data: referralInvites,
    isLoading: invitesLoading,
    refetch: refetchInvites,
  } = useQuery({
    queryKey: ["referralInvites", user?.uid],
    queryFn: () => getReferralInvites(1),
    enabled: !!user?.uid,
  });

  const handleRefresh = () => {
    refetchSummary();
    refetchInvites();
    message.success("Referral data refreshed");
  };

  return (
    <Container>
      <PageHeader
        title="Referrals"
        subtitle="Earn rewards by referring other operators to Clumse"
        icon={GiftOutlined}
      />

      <ReferralSummaryCard loading={summaryLoading} data={referralSummary} />

      <ReferralsCard>
        <CardHeader>
          <CardTitle>
            <UserAddOutlined />
            Your Referrals
          </CardTitle>
          <RefreshButton onClick={handleRefresh}>Refresh</RefreshButton>
        </CardHeader>

        <ReferralInvitesTable loading={invitesLoading} data={referralInvites} />
      </ReferralsCard>

      <ShareCard>
        <ShareContent>
          <ShareIcon>
            <ShareAltOutlined />
          </ShareIcon>
          <ShareText>
            <ShareTitle>Ready to start earning?</ShareTitle>
            <ShareDescription>
              Share your referral link with other operators and asset
              managers. Both of you will receive rewards when they sign up!
            </ShareDescription>
          </ShareText>
        </ShareContent>
      </ShareCard>

      <AgentProgramCard>
        <AgentContent>
          <AgentIcon>
            <TeamOutlined />
          </AgentIcon>
          <AgentText>
            <AgentTitle>Are you a real estate agent?</AgentTitle>
            <AgentDescription>
              Join our agent partnership program to earn $25 per referral and
              offer your clients a 25% discount.
            </AgentDescription>
            <AgentButton href="/agents" target="_blank">
              Learn About Agent Program
            </AgentButton>
          </AgentText>
        </AgentContent>
      </AgentProgramCard>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const ReferralsCard = styled(Card)`
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 8px;

  .anticon {
    color: var(--color-primary);
  }
`;

const RefreshButton = styled(Button)`
  color: var(--color-primary);
  border-color: var(--color-primary);

  &:hover {
    background: var(--color-primary);
    color: white;
  }
`;

const ShareCard = styled(Card)`
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-light) 100%
  );
  border: none;

  .ant-card-body {
    padding: 32px;
  }
`;

const ShareContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  color: white;
`;

const ShareIcon = styled.div`
  font-size: 48px;
  opacity: 0.9;
`;

const ShareText = styled.div`
  flex: 1;
`;

const ShareTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: white;
`;

const ShareDescription = styled.p`
  margin: 0;
  font-size: 16px;
  opacity: 0.9;
  line-height: 1.5;
`;

const AgentProgramCard = styled(Card)`
  margin-top: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;

  .ant-card-body {
    padding: 32px;
  }
`;

const AgentContent = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const AgentIcon = styled.div`
  font-size: 48px;
  color: var(--color-primary);
  opacity: 0.9;
`;

const AgentText = styled.div`
  flex: 1;
`;

const AgentTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--color-text);
`;

const AgentDescription = styled.p`
  margin: 0 0 16px 0;
  font-size: 16px;
  color: var(--color-text-light);
  line-height: 1.5;
`;

const AgentButton = styled.a`
  display: inline-block;
  padding: 12px 24px;
  background: var(--color-primary);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-primary-light);
    color: white;
    text-decoration: none;
    transform: translateY(-1px);
  }
`;

export default Referrals;
